/** ItemList parser: Control base, ItemList's own members, and the `item_N/*` row family. */
import { unquoteString } from '../../../../parser/utils';
import { parseOptionalBool, parseOptionalFloat, parseOptionalInt, parseOptionalVector2i, } from '../../../../parser/valueParsers';
import { parseControl } from '../control/parser';
import { arrayLiteralBody } from '../../../../godot/variant/variantParser.js';
import { boolSlotValue } from '../../../../godot/variant/variantBool.js';
import { holdsResource } from '../../../../godot/scene/resourceLiteral.js';
import { dropTrailingComma, splitTopLevel } from '../../../../godot/string/string.js';
import { indexedElements } from '../../../../godot/classdb/indexedKey.js';
import { ITEM_LIST_ITEMS } from '../../../../godot/classdb/propertyListFamilies.js';
import { slotCount } from '../../../../godot/variant/intSlots.js';
import { MAX_WALKED_ELEMENTS } from '../shared/countWalk';
/**
 * `item_N/*` (`PropertyListHelper`, `item_list.cpp:2461-2467`) for `N < item_count`.
 * Each setter opens with `ERR_FAIL_INDEX(p_idx, items.size())` (as `set_item_text`,
 * `item_list.cpp:93`), so an index past the count is a dropped write, never a grow.
 */
function parseItems(properties, itemCount) {
    const count = Math.min(itemCount, MAX_WALKED_ELEMENTS);
    if (count <= 0)
        return [];
    const declared = indexedElements(properties, ITEM_LIST_ITEMS.prefix, ITEM_LIST_ITEMS.indexParse);
    const items = [];
    for (let i = 0; i < count; i++) {
        const leaves = declared.get(i);
        const item = {};
        const rawText = leaves?.get('text');
        if (rawText !== undefined)
            item.text = unquoteString(rawText);
        const rawIcon = leaves?.get('icon');
        if (holdsResource(rawIcon))
            item.icon = rawIcon;
        const rawSelectable = leaves?.get('selectable');
        if (rawSelectable !== undefined)
            item.selectable = boolSlotValue(rawSelectable) === true;
        const rawDisabled = leaves?.get('disabled');
        if (rawDisabled !== undefined)
            item.disabled = boolSlotValue(rawDisabled) === true;
        items.push(item);
    }
    return items;
}
/**
 * `ItemList::_set`'s deprecated fallback (`item_list.cpp:2242-2259`): a flat
 * `[text, icon, disabled, …]` array. A wrong arity refuses the whole write before
 * `clear()` (`ERR_FAIL_COND_V`, `:2246`), so it yields no rows. `selectable` keeps
 * `Item`'s default, true (`item_list.h:65`).
 */
function parseDeprecatedItems(value) {
    const body = arrayLiteralBody(value);
    if (body === null)
        return null;
    const entries = dropTrailingComma(splitTopLevel(body));
    if (entries.length % 3 !== 0)
        return null;
    const items = [];
    for (let i = 0; i < entries.length; i += 3) {
        const icon = entries[i + 1].trim();
        const item = { text: unquoteString(entries[i].trim()) };
        if (holdsResource(icon))
            item.icon = icon;
        if (boolSlotValue(entries[i + 2].trim()) === true)
            item.disabled = true;
        items.push(item);
    }
    return items;
}
export function parseItemList(heading, properties) {
    const rawItemCount = properties[ITEM_LIST_ITEMS.countKey.key];
    const itemCount = slotCount(rawItemCount) ?? 0;
    // Godot's own saver never writes both forms: item_count/item_N/* replaced
    // `items` outright. So a file carrying `item_count` always wins, and the
    // legacy array is read only for a pure old-format file.
    const legacyItems = rawItemCount === undefined && properties.items !== undefined
        ? parseDeprecatedItems(properties.items)
        : null;
    return {
        ...parseControl(heading, properties),
        selectMode: parseOptionalInt(properties.select_mode),
        iconMode: parseOptionalInt(properties.icon_mode),
        scrollHintMode: parseOptionalInt(properties.scroll_hint_mode),
        allowReselect: parseOptionalBool(properties.allow_reselect),
        allowRmbSelect: parseOptionalBool(properties.allow_rmb_select),
        allowSearch: parseOptionalBool(properties.allow_search),
        autoWidth: parseOptionalBool(properties.auto_width),
        autoHeight: parseOptionalBool(properties.auto_height),
        wraparoundItems: parseOptionalBool(properties.wraparound_items),
        tileScrollHint: parseOptionalBool(properties.tile_scroll_hint),
        sameColumnWidth: parseOptionalBool(properties.same_column_width),
        maxTextLines: parseOptionalInt(properties.max_text_lines),
        itemCount: parseOptionalInt(rawItemCount),
        maxColumns: parseOptionalInt(properties.max_columns),
        fixedColumnWidth: parseOptionalInt(properties.fixed_column_width),
        iconScale: parseOptionalFloat(properties.icon_scale),
        fixedIconSize: parseOptionalVector2i(properties.fixed_icon_size, 'fixed_icon_size'),
        textOverrunBehavior: parseOptionalInt(properties.text_overrun_behavior),
        items: legacyItems ?? parseItems(properties, itemCount),
        // No `item_count` key exists on a legacy file; stand in with the row
        // count `add_item` would have left `get_item_count()` at.
        ...(legacyItems ? { itemCount: legacyItems.length } : {}),
    };
}
//# sourceMappingURL=parser.js.map